import {
  Avatar,
  Link as ButtonLink,
  Checkbox,
  CircularProgress,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
} from '@material-ui/core';
import MonetizationOnOutlinedIcon from '@mui/icons-material/MonetizationOnOutlined';
import { format } from 'date-fns';
import React from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { selectLocations } from 'entities/location/selectors';
import { selectUserLocations } from 'entities/user/selectors';
import VolunteerTime from 'entities/volunteerTime/model';
import { EligibilityFormStatus } from 'generated/graphql';
import { primaryBlue } from 'utils/color-styles';
import { convertDuration } from 'utils/convertDuration';
import { CurrencyCode, currencyFormat } from 'utils/currencyFormat';

const styles = {
  nameLink: {
    width: 110,
    display: 'block',
    lineHeight: '32px',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
  },
};

export const VolunteerTimesTableBody = ({
  volunteerTimes,
  statusUpdating,
  selectedItems,
  setSelectedItems,
  anyStatusUpdating,
  setVolunteerTimeDetails,
  setModalOpen,
}: {
  volunteerTimes?: VolunteerTime[] | null;
  statusUpdating: Map<string, boolean>;
  selectedItems: string[];
  anyStatusUpdating: boolean;
  setSelectedItems: React.Dispatch<React.SetStateAction<string[]>>;
  setVolunteerTimeDetails: React.Dispatch<React.SetStateAction<VolunteerTime | undefined>>;
  setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
  const onClickHandler = (e) => e.stopPropagation();
  const userLocations = useSelector(selectUserLocations);
  const locations = useSelector(selectLocations);
  const allLocations = userLocations.concat(locations);

  const handleCheckboxChange = (id: string) => {
    setSelectedItems((prevSelectedItems) => {
      if (prevSelectedItems.includes(id)) {
        return prevSelectedItems.filter((itemId) => itemId !== id);
      } else {
        return [...prevSelectedItems, id];
      }
    });
  };
  return (
    <TableBody>
      {volunteerTimes?.length &&
        volunteerTimes.map((volunteerTime: VolunteerTime) => {
          const locationId = volunteerTime.User?.locationId;
          return (
            <TableRow key={volunteerTime.id}>
              <TableCell>
                {statusUpdating.get(volunteerTime.id) ? (
                  <CircularProgress size={24} style={{ margin: '10px 12px' }} />
                ) : (
                  <Checkbox
                    color="primary"
                    checked={selectedItems.includes(volunteerTime.id)}
                    onChange={() => handleCheckboxChange(volunteerTime.id)}
                    disabled={anyStatusUpdating || volunteerTime.approval !== 'Pending'}
                  />
                )}
              </TableCell>
              <TableCell>
                <Avatar
                  alt={`${volunteerTime.User.fullName?.first} ${volunteerTime.User.fullName?.last}`}
                  src={volunteerTime.User.mainPicture?.replace('http://', 'https://')}
                  style={{
                    width: 32,
                    height: 32,
                    float: 'left',
                    marginRight: 10,
                  }}
                />
                <Link
                  to={`/volunteers/${volunteerTime.User.id}`}
                  className="hoverable"
                  style={styles.nameLink}
                  onClick={onClickHandler}
                >
                  {volunteerTime.User.fullName?.first} {volunteerTime.User.fullName?.last}
                </Link>
              </TableCell>
              <TableCell>
                {locationId && allLocations?.size && allLocations.get(locationId)
                  ? `${[
                      allLocations.get(locationId)?.name,
                      allLocations.get(locationId)?.state,
                      allLocations.get(locationId)?.country,
                    ]
                      .filter(Boolean)
                      .join(', ')}`
                  : '-'}
              </TableCell>
              <TableCell>{volunteerTime.Deed?.type || 'External'}</TableCell>
              <TableCell>
                {volunteerTime.Deed?.name ||
                  `External volunteering${
                    volunteerTime.Nonprofit?.name || volunteerTime.externalNonprofitName
                      ? ` for ${
                          volunteerTime.Nonprofit?.name || volunteerTime.externalNonprofitName
                        }`
                      : ''
                  }`}
              </TableCell>
              <TableCell style={{ alignItems: 'center', justifyContent: 'center' }}>
                {convertDuration(volunteerTime.duration)}
                {` `}
                on{` `}
                {format(new Date(volunteerTime.date), 'MMM dd, yyyy')}
                {` `}
                {volunteerTime.VolunteerReward?.formStatus &&
                  [EligibilityFormStatus.Eligible, EligibilityFormStatus.NotRequired].includes(
                    volunteerTime.VolunteerReward?.formStatus
                  ) && (
                    <Tooltip
                      placement="right"
                      title={`This volunteering time may be eligible for a volunteer grant of $${
                        volunteerTime?.VolunteerReward?.amount &&
                        currencyFormat(
                          volunteerTime.VolunteerReward.amount,
                          volunteerTime.VolunteerReward.currency as CurrencyCode
                        )
                      }, pending approval from this user's employer`}
                    >
                      <MonetizationOnOutlinedIcon sx={{ color: primaryBlue }} />
                    </Tooltip>
                  )}
              </TableCell>
              <TableCell>
                <ButtonLink
                  component="button"
                  style={{
                    color: primaryBlue,
                    cursor: 'pointer',
                    outline: 'none',
                  }}
                  onClick={() => {
                    setVolunteerTimeDetails(volunteerTime);
                    setModalOpen(true);
                  }}
                >
                  Details
                </ButtonLink>
              </TableCell>
            </TableRow>
          );
        })}
    </TableBody>
  );
};
